import { useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Activity, BellDot, Bot, Gauge, ScrollText, TriangleAlert } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSuperAdminDebugOverlay } from '@/hooks/useSuperAdminDebugOverlay';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import {
  setSuperAdminDebugOverlayEnabled,
  useSuperAdminDebugOverlayEnabledAtPreference,
  useSuperAdminDebugOverlayPreference,
} from '@/lib/superAdminDebugOverlayPreference';
import { cn } from '@/lib/utils';

type SurfaceName = 'admin' | 'customer' | 'msp' | 'vendor';
const CHANGE_PULSE_DURATION_MS = 900;
const CHANGE_PULSE_COUNT = 2;
const CHANGE_PULSE_TOTAL_MS = CHANGE_PULSE_DURATION_MS * CHANGE_PULSE_COUNT;

function formatCompactRelativeTime(date: Date) {
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));

  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function resolveSurface(pathname: string, isMspContext: boolean): SurfaceName {
  if (pathname.startsWith('/admin')) {
    return 'admin';
  }

  if (pathname.startsWith('/vendor')) {
    return 'vendor';
  }

  if (isMspContext || pathname.startsWith('/customers') || pathname.startsWith('/msp-')) {
    return 'msp';
  }

  return 'customer';
}

function SummaryInline({
  icon: Icon,
  children,
  value,
  highlighted = false,
  showLabel = true,
}: {
  icon: typeof Activity;
  children: string;
  value: string;
  highlighted?: boolean;
  showLabel?: boolean;
}) {
  return (
    <span
      className="flex items-center gap-1 whitespace-nowrap text-[11px] text-muted-foreground"
      aria-label={`${children} ${value}`}
      title={`${children} ${value}`}
    >
      <Icon
        className={cn(
          'h-3.5 w-3.5 shrink-0 transition-colors duration-500',
          highlighted ? 'text-primary animate-pulse' : 'text-foreground/80',
        )}
        style={highlighted ? {
          animationDuration: `${CHANGE_PULSE_DURATION_MS}ms`,
          animationIterationCount: CHANGE_PULSE_COUNT,
        } : undefined}
      />
      {showLabel ? <span>{children}</span> : <span className="sr-only">{children}</span>}
      <span
        className={cn(
          'px-1 py-0.5 font-semibold text-foreground transition-all duration-500',
          highlighted
            ? 'text-primary -translate-y-[1px] scale-[1.05] animate-pulse'
            : '',
        )}
        style={highlighted ? {
          animationDuration: `${CHANGE_PULSE_DURATION_MS}ms`,
          animationIterationCount: CHANGE_PULSE_COUNT,
        } : undefined}
      >
        {value}
      </span>
    </span>
  );
}

function useAnimatedNumber(target: number, enabled: boolean) {
  const [value, setValue] = useState(target);

  useEffect(() => {
    if (!enabled) {
      setValue(target);
      return;
    }

    let frame = 0;
    const startValue = value;
    const delta = target - startValue;
    if (delta === 0) {
      return;
    }

    const startedAt = performance.now();
    const duration = 450;

    const tick = (now: number) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - (1 - progress) * (1 - progress);
      setValue(Math.round(startValue + delta * eased));
      if (progress < 1) {
        frame = window.requestAnimationFrame(tick);
      }
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [enabled, target]);

  return value;
}

function useAnimatedNumbers(targets: number[], enabled: boolean) {
  const [values, setValues] = useState(targets);
  const valuesRef = useRef(targets);
  valuesRef.current = values;

  const targetsKey = targets.join(',');

  useEffect(() => {
    if (!enabled) {
      setValues(targets);
      return;
    }

    const startValues = valuesRef.current;
    const deltas = targets.map((target, index) => target - (startValues[index] ?? target));
    if (deltas.every((delta) => delta === 0) && startValues.length === targets.length) {
      return;
    }

    let frame = 0;
    const startedAt = performance.now();
    const duration = 450;

    const tick = (now: number) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - (1 - progress) * (1 - progress);
      const next = targets.map((target, index) =>
        Math.round((startValues[index] ?? target) + deltas[index] * eased),
      );
      setValues(next);
      valuesRef.current = next;
      if (progress < 1) {
        frame = window.requestAnimationFrame(tick);
      }
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [enabled, targetsKey]);

  return values;
}

function useHighlightOnChange(signature: string, enabled: boolean) {
  const previousSignature = useRef(signature);
  const [highlighted, setHighlighted] = useState(false);

  useEffect(() => {
    if (!enabled) {
      previousSignature.current = signature;
      setHighlighted(false);
      return;
    }

    if (previousSignature.current === signature) {
      return;
    }

    previousSignature.current = signature;
    setHighlighted(true);

    const timeout = window.setTimeout(() => setHighlighted(false), CHANGE_PULSE_TOTAL_MS);
    return () => window.clearTimeout(timeout);
  }, [enabled, signature]);

  return highlighted;
}

function AnimatedBudgetValue({
  used,
  enabled,
}: {
  used: number;
  enabled: boolean;
}) {
  const animatedUsed = useAnimatedNumber(used, enabled);

  return <>{animatedUsed}</>;
}

function AnimatedSummaryInline({
  icon,
  label,
  used,
  enabled,
}: {
  icon: typeof Activity;
  label: string;
  used: number;
  enabled: boolean;
}) {
  const highlighted = useHighlightOnChange(`${label}:${used}`, enabled);
  const animatedUsed = useAnimatedNumber(used, enabled);

  return (
    <SummaryInline
      icon={icon}
      children={label}
      value={`${animatedUsed}`}
      highlighted={highlighted}
      showLabel={false}
    />
  );
}

function AnimatedCountInline({
  icon,
  label,
  values,
  suffix,
  enabled,
}: {
  icon: typeof Activity;
  label: string;
  values: number[];
  suffix: string;
  enabled: boolean;
}) {
  const highlighted = useHighlightOnChange(`${label}:${values.join(':')}`, enabled);
  const animatedValues = useAnimatedNumbers(values, enabled);

  return (
    <SummaryInline
      icon={icon}
      children={label}
      value={`${animatedValues.join(' / ')}${suffix}`}
      highlighted={highlighted}
      showLabel={false}
    />
  );
}

function TooltipMetricLabel({
  icon: Icon,
  label,
}: {
  icon: typeof Activity;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      <Icon className="h-3.5 w-3.5 shrink-0 text-foreground/80" />
      <span>{label}</span>
    </span>
  );
}

export function SuperAdminDebugOverlay() {
  const enabled = useSuperAdminDebugOverlayPreference();
  const enabledAt = useSuperAdminDebugOverlayEnabledAtPreference();
  const location = useLocation();
  const { user, organization, isMspContext } = useOrganization();
  const isSuperAdmin = useIsSuperAdmin(user?.id);

  const surface = useMemo(
    () => resolveSurface(location.pathname, isMspContext),
    [isMspContext, location.pathname],
  );

  const { data, isLoading, error } = useSuperAdminDebugOverlay({
    enabled: enabled && isSuperAdmin && Boolean(enabledAt),
    organizationId: organization?.id ?? null,
    surface,
    pathname: `${location.pathname}${location.search}${location.hash}`,
    enabledAt: enabledAt ?? new Date().toISOString(),
  });

  const [budgetBaseline, setBudgetBaseline] = useState<{
    enabledAt: string;
    providers: Record<string, number>;
    aiTokensUsed: number | null;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !enabledAt || !data) {
      setBudgetBaseline(null);
      return;
    }

    setBudgetBaseline((current) => {
      if (current?.enabledAt === enabledAt) {
        return current;
      }

      return {
        enabledAt,
        providers: Object.fromEntries(
          data.apiBudget.providers.map((provider) => [provider.provider, provider.creditsUsed]),
        ),
        aiTokensUsed: data.apiBudget.aiAgent?.tokensUsedThisMonth ?? null,
      };
    });
  }, [data, enabled, enabledAt]);

  const [, setNowTick] = useState(0);

  useEffect(() => {
    if (!enabled || !data?.generatedAt) {
      return;
    }

    const interval = window.setInterval(() => {
      setNowTick((tick) => tick + 1);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [data?.generatedAt, enabled]);

  if (!isSuperAdmin) {
    return null;
  }

  const providerSummaries = (data?.apiBudget.providers ?? []).slice(0, 5).map((provider) => ({
    ...provider,
    creditsUsed: Math.max(
      0,
      provider.creditsUsed - (budgetBaseline?.providers[provider.provider] ?? provider.creditsUsed),
    ),
  }));
  const aiAgentUsage = data?.apiBudget.aiAgent
    ? {
        ...data.apiBudget.aiAgent,
        tokensUsedThisMonth: Math.max(
          0,
          data.apiBudget.aiAgent.tokensUsedThisMonth - (budgetBaseline?.aiTokensUsed ?? data.apiBudget.aiAgent.tokensUsedThisMonth),
        ),
      }
    : null;

  const updatedLabel = data?.generatedAt
    ? formatCompactRelativeTime(new Date(data.generatedAt))
    : '0s ago';
  const trackingSinceLabel = enabledAt
    ? formatDistanceToNow(new Date(enabledAt), { addSuffix: true })
    : null;

  return (
    <div
      data-testid="super-admin-live-statistics"
      className="flex items-center gap-1.5"
    >
      {enabled ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="hidden max-w-[min(70vw,64rem)] min-w-0 flex-col items-end gap-0.5 text-[11px] lg:flex"
              data-testid="super-admin-live-statistics-strip"
              tabIndex={0}
              aria-label="Live Statistics details"
            >
              <div className="flex max-w-full items-center gap-2 overflow-x-auto rounded-full px-1">
                {data ? (
                  <>
                    <AnimatedCountInline
                      icon={ScrollText}
                      label="Audit"
                      values={[data.audit.last30Minutes]}
                      suffix=""
                    enabled={enabled && !error}
                  />
                    <span className="text-border">•</span>
                    <AnimatedCountInline
                      icon={BellDot}
                      label="Notifications"
                      values={[data.deliveries.notificationsLast30Minutes]}
                      suffix=""
                      enabled={enabled && !error}
                    />
                    <span className="text-border">•</span>
                    <AnimatedCountInline
                      icon={TriangleAlert}
                      label="Alerts"
                      values={[data.deliveries.alertsLast30Minutes]}
                      suffix=""
                      enabled={enabled && !error}
                    />
                  </>
                ) : null}
                {providerSummaries.map((provider, index) => (
                  <span key={provider.provider} className="contents">
                    {(data || index > 0) ? <span className="text-border">•</span> : null}
                    <AnimatedSummaryInline
                      icon={Gauge}
                      label={provider.provider}
                      used={provider.creditsUsed}
                      enabled={enabled && !error}
                    />
                  </span>
                ))}
                {aiAgentUsage ? (
                  <>
                    <span className="text-border">•</span>
                    <AnimatedSummaryInline
                      icon={Bot}
                      label="AI"
                      used={aiAgentUsage.tokensUsedThisMonth}
                      enabled={enabled && !error}
                    />
                  </>
                ) : null}
                {isLoading ? (
                  <span className="shrink-0 whitespace-nowrap text-[11px] text-muted-foreground">Loading…</span>
                ) : null}
                {error ? (
                  <span className="shrink-0 whitespace-nowrap text-[11px] text-destructive">Snapshot unavailable</span>
                ) : null}
              </div>
              <div className="self-start px-1 text-left text-[11px] leading-tight text-muted-foreground">
                <span className="whitespace-nowrap">Updated</span>
                <span className="ml-1 whitespace-nowrap">{updatedLabel}</span>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" className="max-w-md p-3">
            <div className="space-y-3 text-xs">
              <div className="space-y-1">
                <p className="font-semibold text-foreground">Live Statistics</p>
                <p className="text-muted-foreground">Super admin only testing surface</p>
                {trackingSinceLabel ? (
                  <p className="text-muted-foreground">
                    Showing incremental numbers since {trackingSinceLabel}
                  </p>
                ) : null}
                {error ? <p className="text-muted-foreground">Snapshot unavailable</p> : null}
              </div>
              {data ? (
                <>
                  <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
                    <TooltipMetricLabel icon={ScrollText} label="Audit" />
                    <span className="text-foreground">{data.audit.last30Minutes}</span>
                    <TooltipMetricLabel icon={BellDot} label="Notifications" />
                    <span className="text-foreground">{data.deliveries.notificationsLast30Minutes}</span>
                    <TooltipMetricLabel icon={TriangleAlert} label="Alerts" />
                    <span className="text-foreground">{data.deliveries.alertsLast30Minutes}</span>
                    {providerSummaries.map((provider) => (
                      <span key={`${provider.provider}-tooltip`} className="contents">
                        <TooltipMetricLabel icon={Gauge} label={provider.provider} />
                        <span className="text-foreground">
                          <AnimatedBudgetValue
                            used={provider.creditsUsed}
                            enabled={enabled && !error}
                          /> calls
                        </span>
                      </span>
                    ))}
                    {data.apiBudget.aiAgent ? (
                      <>
                        <TooltipMetricLabel icon={Bot} label="AI" />
                        <span className="text-foreground">
                          <AnimatedBudgetValue
                            used={aiAgentUsage?.tokensUsedThisMonth ?? 0}
                            enabled={enabled && !error}
                          /> tokens
                        </span>
                      </>
                    ) : null}
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium text-foreground">Latest alerts and notifications</p>
                    {data.deliveries.recentDeliveries.slice(0, 5).map((delivery, index) => (
                      <div key={delivery.id} className="space-y-0.5 text-muted-foreground">
                        <p className="break-words pl-4 -indent-4">{index + 1}. {delivery.title} · {delivery.status}</p>
                        <p className="text-[11px]">{formatDistanceToNow(new Date(delivery.createdAt), { addSuffix: true })}</p>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium text-foreground">Latest audits</p>
                    {data.audit.recentEvents.slice(0, 5).map((event, index) => (
                      <div key={event.id} className="space-y-0.5 text-muted-foreground">
                        <p className="break-words pl-4 -indent-4">{index + 1}. {(event.actorName ?? event.description)} · {event.actionType}</p>
                        <p className="text-[11px]">{formatDistanceToNow(new Date(event.createdAt), { addSuffix: true })}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground">Waiting for the latest snapshot.</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      ) : null}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className={cn(
              'relative h-10 w-10 rounded-full',
              enabled
                ? error
                  ? 'text-destructive hover:text-destructive'
                  : 'text-primary hover:text-primary'
                : 'text-muted-foreground',
            )}
            aria-label={enabled ? 'Hide Live Statistics' : 'Show Live Statistics'}
            onClick={() => setSuperAdminDebugOverlayEnabled(!enabled)}
          >
            <Activity className="h-4 w-4" />
            {enabled ? (
              <>
                <span
                  className={cn(
                    'absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full animate-ping opacity-60',
                    error ? 'bg-destructive' : 'bg-primary',
                  )}
                />
                <span
                  className={cn(
                    'absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full',
                    error ? 'bg-destructive' : 'bg-primary',
                  )}
                />
              </>
            ) : null}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end">Show or hide live statistics</TooltipContent>
      </Tooltip>
    </div>
  );
}
